import { create } from "zustand";
import type { WorkspaceSearchFileResultDto, WorkspaceSearchMatchDto } from "@/lib/api";

export type WorkspaceSearchStatus = "idle" | "loading" | "done" | "error";

interface SearchState {
	searchOpen: boolean;
	query: string;
	caseSensitive: boolean;
	status: WorkspaceSearchStatus;
	files: WorkspaceSearchFileResultDto[];
	matchCount: number;
	truncated: boolean;
	error: string | null;
	/**
	 * 新しい検索結果が来た (loading 開始・結果反映・クリア等) たびに進む世代カウンタ。
	 * `updateFileMatches` によるライブ編集反映では進めない。UI 側 (折りたたみ・選択状態) は
	 * これをきっかけにリセットする想定で、ライブ編集のたびに選択が消えるのを防ぐ。
	 */
	resultsVersion: number;
	openSearch: () => void;
	closeSearch: () => void;
	setQuery: (query: string) => void;
	toggleCaseSensitive: () => void;
	setLoading: () => void;
	setResults: (
		files: WorkspaceSearchFileResultDto[],
		matchCount: number,
		truncated: boolean,
	) => void;
	setError: (message: string) => void;
	clearResults: () => void;
	reset: () => void;
	/**
	 * 開いているファイルの編集を反映して、指定パスの一致一覧を丸ごと差し替える。`matches` が
	 * 空ならその行の一致がすべて消えたということなので、結果一覧からそのファイル自体を除く。
	 * エディタで検索語がライブにハイライトされる仕組みと対にして、一覧側も追随させるために使う。
	 */
	updateFileMatches: (path: string, matches: WorkspaceSearchMatchDto[]) => void;
}

export const useSearchStore = create<SearchState>()((set) => ({
	searchOpen: false,
	query: "",
	caseSensitive: false,
	status: "idle",
	files: [],
	matchCount: 0,
	truncated: false,
	error: null,
	resultsVersion: 0,
	openSearch: () => set({ searchOpen: true }),
	// searchOpen だけでなく結果もクリアする。query や caseSensitive は保ち、次に開いたときの
	// 入力を残す (結果だけは、閉じている間に確定した古い検索の内容を一瞬でも見せないようにする)。
	closeSearch: () =>
		set((state) => ({
			searchOpen: false,
			status: "idle",
			files: [],
			matchCount: 0,
			truncated: false,
			error: null,
			resultsVersion: state.resultsVersion + 1,
		})),
	setQuery: (query) => set({ query }),
	toggleCaseSensitive: () => set((state) => ({ caseSensitive: !state.caseSensitive })),
	setLoading: () =>
		set((state) => ({
			status: "loading",
			files: [],
			matchCount: 0,
			truncated: false,
			error: null,
			resultsVersion: state.resultsVersion + 1,
		})),
	setResults: (files, matchCount, truncated) =>
		set((state) => ({
			status: "done",
			files,
			matchCount,
			truncated,
			error: null,
			resultsVersion: state.resultsVersion + 1,
		})),
	setError: (error) => set({ status: "error", error }),
	clearResults: () =>
		set((state) => ({
			status: "idle",
			files: [],
			matchCount: 0,
			truncated: false,
			error: null,
			resultsVersion: state.resultsVersion + 1,
		})),
	// ファイルエディタでの編集をライブ反映するだけの更新。折りたたみ/選択状態のリセットは
	// 「新しい検索結果が来た」ときだけ行いたいので、意図的に resultsVersion は進めない。
	updateFileMatches: (path, matches) =>
		set((state) => {
			const exists = state.files.some((f) => f.path === path);
			const files =
				matches.length === 0
					? state.files.filter((f) => f.path !== path)
					: exists
						? state.files.map((f) => (f.path === path ? { path, matches } : f))
						: [...state.files, { path, matches }];
			// sum() は Math.sumPrecise 依存で Chrome/Edge (V8) が未実装のため使えない (#675)。
			// eslint-disable-next-line local/no-reduce-sum
			const matchCount = files.reduce((total, f) => total + f.matches.length, 0);
			return { files, matchCount };
		}),
	reset: () =>
		set((state) => ({
			searchOpen: false,
			query: "",
			caseSensitive: false,
			status: "idle",
			files: [],
			matchCount: 0,
			truncated: false,
			error: null,
			resultsVersion: state.resultsVersion + 1,
		})),
}));
